import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ACTIONS = [
  { action: "explain", label: "Explain" },
  { action: "summarize", label: "Executive summary" },
  { action: "simplify", label: "Simplify language" },
  { action: "missing", label: "Missing sections" },
  { action: "improve", label: "Suggest improvements" },
  { action: "inconsistencies", label: "Find inconsistencies" },
  { action: "risks", label: "Identify risks" },
  { action: "compliance", label: "Compliance check" },
  { action: "negotiation", label: "Negotiation points" },
];

export default function ContractAiDrawer({ contract, open, onClose, onInsertClause }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    setMsgs([]); setInput("");
    if (contract) api.get(`/contracts/${contract.contract_id}/versions`)
      .then(({ data }) => setVersions(data.versions.filter((v) => !v.is_current))).catch(() => setVersions([]));
  }, [contract]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs, loading]);

  const run = async (payload, label, insertable) => {
    if (loading || !contract) return;
    setMsgs((m) => [...m, { role: "user", content: label }]); setLoading(true);
    try {
      const { data } = await api.post(`/contracts/${contract.contract_id}/ai`, payload);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, insertable }]);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "AI unavailable";
      setMsgs((m) => [...m, { role: "assistant", content: msg, error: true }]);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const ask = () => {
    const q = input.trim(); if (!q) return; setInput("");
    run({ action: "question", question: q }, q);
  };
  const genClause = () => {
    const q = input.trim(); if (!q) return; setInput("");
    run({ action: "generate-clause", question: q }, `Draft a clause: ${q}`, true);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="bg-[#0d0d0f] border-white/10 text-white w-full sm:max-w-md p-0 flex flex-col" data-testid="contract-ai-drawer">
        <SheetHeader className="p-5 pb-3 border-b border-white/8">
          <SheetTitle className="font-heading text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D4AF37]" /> AI Contract Assistant</SheetTitle>
          <SheetDescription className="text-[#71717A] truncate">{contract?.title}</SheetDescription>
        </SheetHeader>
        <div className="p-4 border-b border-white/8">
          <p className="text-[11px] uppercase tracking-wider text-[#52525B] mb-2">Quick actions</p>
          <div className="flex flex-wrap gap-1.5">
            {ACTIONS.map((a) => (
              <button key={a.action} onClick={() => run({ action: a.action }, a.label)} disabled={loading}
                data-testid={`cai-action-${a.action}`}
                className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/40 hover:text-[#D4AF37] disabled:opacity-40 transition-colors">{a.label}</button>
            ))}
          </div>
          {versions.length > 0 && (
            <div className="mt-2.5">
              <p className="text-[11px] uppercase tracking-wider text-[#52525B] mb-1.5">Compare version</p>
              <div className="flex flex-wrap gap-1.5">
                {versions.map((v) => (
                  <button key={v.version} onClick={() => run({ action: "compare", compare_version: v.version }, `Compare with v${v.version}`)} disabled={loading}
                    data-testid={`cai-compare-${v.version}`} className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/40 disabled:opacity-40">v{v.version}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="cai-messages">
          {msgs.length === 0 && !loading && (
            <div className="text-center text-sm text-[#52525B] mt-10">
              <Sparkles className="w-8 h-8 mx-auto mb-2 text-[#D4AF37]/40" />
              Ask about this contract, request a new clause, or run a quick action. Generated text is informational, not legal advice.
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`text-sm rounded-xl px-3 py-2.5 whitespace-pre-wrap leading-relaxed ${
              m.role === "user" ? "bg-[#D4AF37]/15 text-white ml-8" : m.error ? "bg-red-500/10 text-red-300 mr-8" : "bg-[#1C1C1F] text-[#E4E4E7] mr-8"}`}>
              {m.content}
              {m.insertable && onInsertClause && (
                <button onClick={() => { onInsertClause(m.content); toast.success("Clause added to contract"); }} data-testid={`cai-insert-${i}`}
                  className="mt-2 flex items-center gap-1 text-[11px] text-[#D4AF37] hover:underline"><Plus className="w-3 h-3" /> Insert as new clause</button>
              )}
            </div>
          ))}
          {loading && <div className="flex items-center gap-2 text-xs text-[#71717A] mr-8"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing…</div>}
        </div>
        <div className="p-4 border-t border-white/8 space-y-2">
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="cai-input" placeholder="Ask or describe a clause to draft…"
              className="flex-1 bg-[#121214] border border-white/10 rounded-full px-4 h-10 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/40" />
            <Button type="submit" disabled={loading} data-testid="cai-send" className="fv-gold-gradient text-[#0A0A0B] h-10 w-10 p-0 rounded-full shrink-0"><Send className="w-4 h-4" /></Button>
          </form>
          <button onClick={genClause} disabled={loading} data-testid="cai-generate-clause" className="w-full text-xs text-[#A1A1AA] hover:text-[#D4AF37] py-1">✨ Draft a clause from the text above</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
